import React from 'react';
import { Box, Divider, FormControlLabel, Stack, Switch } from '@mui/material';

import { DistanceField, NumericField } from '@paragon/material';

import {
  bearingEnvelopeElevationsMatch,
  getActiveLevelOrNull,
  getBearingEnvelopeElevationsFromLevel,
} from '../../../../../../store/selectors/layoutDataSelectors';
import { useAppSelector } from '../../../../../hooks';
import { ProjectHierarchyContextSelects } from '../../ProjectHierarchyContextSelects';
import { SyncElevationButton } from '../../SyncElevationButton';
import { BearingEnvelopeBeamProperties } from './BearingEnvelopeBeamProperties';
import type { BearingEnvelopePropertiesProps } from './BearingEnvelopeProperties';

export function getThicknessAndStructuralToggleRowSx(columnCount: number) {
  return {
    display: 'grid', // need to force an even division via a grid because `FormattedDistanceInput` forces full width
    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
    alignItems: 'center',
    gap: 1,
  } as const;
}

export function BearingEnvelopeCommonProperties(props: BearingEnvelopePropertiesProps) {
  const activeLevel = useAppSelector(getActiveLevelOrNull);
  const elevationsFromLevel =
    activeLevel == null ? {} : getBearingEnvelopeElevationsFromLevel(activeLevel, props.properties);
  const isBeam = props.properties.bearingType === 'Beam';

  return (
    <>
      <ProjectHierarchyContextSelects
        levelGuid={props.properties.levelGuid}
        onChange={(levelGuid) => {
          props.onChange({ levelGuid });
        }}
        disabled={props.disabled}
      />

      <Divider />

      <BearingEnvelopeBeamProperties
        disabled={props.disabled}
        properties={props.properties}
        onChange={props.onChange}
      />

      <Stack direction="row" spacing={1} sx={{ paddingTop: 1 }}>
        <DistanceField
          label="Top Elevation"
          value={props.properties.top}
          disabled={props.disabled}
          onSubmit={(top) => {
            props.onChange({ top });
          }}
        />

        <DistanceField
          label="Bottom Elevation"
          value={props.properties.bottom}
          disabled={props.disabled}
          onSubmit={(bottom) => {
            props.onChange({ bottom });
          }}
        />
      </Stack>

      <SyncElevationButton
        plural // because bearing envelopes have a top and bottom elevation
        disabled={props.disabled}
        visible={
          props.properties.levelGuid != null && elevationsFromLevel.top != null && elevationsFromLevel.bottom != null
        }
        synchronized={bearingEnvelopeElevationsMatch(props.properties, elevationsFromLevel)}
        onSynchronize={() => {
          props.onChange(elevationsFromLevel);
        }}
      />

      <Divider />

      <Box
        sx={[
          getThicknessAndStructuralToggleRowSx(2),
          { marginTop: '0!important' /* margin will come from the distance input */ },
        ]}
      >
        <DistanceField
          label="Thickness"
          disabled={props.disabled}
          min={1 / 16}
          value={props.properties.thickness}
          onSubmit={(thickness) => props.onChange({ thickness })}
        />

        {isBeam ? (
          <NumericField
            label="Plies"
            disabled={props.disabled}
            min={1}
            integers
            value={props.properties.numberOfPlies}
            onSubmit={(numberOfPlies) => props.onChange({ numberOfPlies })}
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              label="Structural"
              control={
                <Switch
                  checked={!props.properties.nonStructural}
                  onChange={() => {
                    props.onChange({ nonStructural: !props.properties.nonStructural });
                  }}
                />
              }
              disabled={props.disabled}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
